import { useCallback, useEffect, useEffectEvent, useRef, useState } from 'react';
import type { RefObject, UIEvent } from 'react';

export type TodayJumpDirection = 'left' | 'right' | null;

export interface ITimelineScrollSync {
  /** Frozen group-list pane (vertical scroll mirrored from the timeline). */
  frozenRef: RefObject<HTMLDivElement | null>;
  /** Timeline scroller — owns both scrollbars. */
  scrollerRef: RefObject<HTMLDivElement | null>;
  /** Direction toward today when it is scrolled out of view, else null. */
  todayJump: TodayJumpDirection;
  /** onScroll handler for the timeline scroller. */
  onScroll: (event: UIEvent<HTMLDivElement>) => void;
  /** Smooth-scroll today into the centre of the viewport. */
  scrollToToday: () => void;
}

/** A line of wheel delta in line mode (deltaMode === 1) ≈ this many pixels. */
const LINE_HEIGHT_PX = 16;

/** Two-pane scroll sync for the Gantt: mirrors the timeline's vertical scroll
 *  onto the frozen list, forwards wheel-over-the-list to the timeline, and
 *  tracks whether the today line is scrolled out of view (so a jump button can
 *  point toward it). `widthKey` must change whenever the track width does
 *  (granularity / schedule) so today visibility is recomputed after relayout. */
export function useTimelineScrollSync(todayFrac: number | null, widthKey: unknown): ITimelineScrollSync {
  const frozenRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [todayJump, setTodayJump] = useState<TodayJumpDirection>(null);

  const recompute = useCallback(() => {
    const el = scrollerRef.current;
    if (!el || todayFrac === null) {
      setTodayJump(null);
      return;
    }
    const todayPx = todayFrac * el.scrollWidth;
    if (todayPx < el.scrollLeft) setTodayJump('left');
    else if (todayPx > el.scrollLeft + el.clientWidth) setTodayJump('right');
    else setTodayJump(null);
  }, [todayFrac]);

  const scrollToToday = useCallback(() => {
    const el = scrollerRef.current;
    if (!el || todayFrac === null) return;
    el.scrollTo({ left: todayFrac * el.scrollWidth - el.clientWidth / 2, behavior: 'smooth' });
  }, [todayFrac]);

  const onScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const pane = frozenRef.current;
      if (pane) pane.scrollTop = event.currentTarget.scrollTop;
      recompute();
    },
    [recompute],
  );

  // The frozen list has no scrollbar of its own, so wheeling over it forwards to
  // the timeline scroller. `deltaMode` is normalized so physical wheels (line
  // mode on Windows) scroll a sensible amount, not ~3px. Non-passive so
  // preventDefault stops the page behind from scrolling instead.
  useEffect(() => {
    const pane = frozenRef.current;
    if (!pane) return;
    const onWheel = (event: WheelEvent) => {
      const scroller = scrollerRef.current;
      if (!scroller) return;
      const deltaY =
        event.deltaMode === 1 ? event.deltaY * LINE_HEIGHT_PX :
        event.deltaMode === 2 ? event.deltaY * scroller.clientHeight :
        event.deltaY;
      scroller.scrollTop += deltaY;
      event.preventDefault();
    };
    pane.addEventListener('wheel', onWheel, { passive: false });
    return () => pane.removeEventListener('wheel', onWheel);
  }, []);

  // Resize listener registered once; reads the latest `recompute` via an Effect
  // Event so data refreshes (todayFrac changes) don't tear it down and re-add it.
  const handleResize = useEffectEvent(() => recompute());
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Recompute today visibility on mount and whenever the track width / today
  // position changes.
  useEffect(() => {
    recompute();
  }, [recompute, widthKey]);

  return { frozenRef, scrollerRef, todayJump, onScroll, scrollToToday };
}
